import { useEffect, useRef } from "react";
import { create } from "zustand";
import type {
  KeyboardShortcutBinding,
  KeyboardShortcutCommand,
  KeyboardShortcutPreferences,
  KeyboardShortcutModifier,
} from "@rivets/core";
import { client } from "@/lib/api";

export const TASK_SHORTCUT_COMMANDS = [
  "selectTask1",
  "selectTask2",
  "selectTask3",
  "selectTask4",
  "selectTask5",
  "selectTask6",
  "selectTask7",
  "selectTask8",
  "selectTask9",
] as const;

export type TaskShortcutCommand = (typeof TASK_SHORTCUT_COMMANDS)[number];
export type ShortcutCommand = KeyboardShortcutCommand;
export type ShortcutBinding = KeyboardShortcutBinding;

export type ShortcutBindings = Record<ShortcutCommand, ShortcutBinding>;

const SHORTCUT_STORAGE_PREFIX = "rivets.keyboard-shortcuts.v2";
const MODIFIER_ORDER: KeyboardShortcutModifier[] = ["command", "option", "shift", "control"];

/**
 * Default bindings live separately from command handlers so a future settings
 * layer can replace this table without changing the components that perform
 * the commands.
 */
export const DEFAULT_SHORTCUT_BINDINGS: ShortcutBindings = {
  closeChatTab: { key: "w", modifiers: ["command"] },
  commandPalette: { key: "k", modifiers: ["command"] },
  copyConciseTranscript: { key: "c", modifiers: ["command", "option"] },
  findInTranscript: { key: "f", modifiers: ["command"] },
  focusPromptComposer: { key: "l", modifiers: ["command"] },
  /* The chat tabs keep ⌘[ / ⌘]; history takes the same brackets with Option
     added. Arrows were the first choice and lost: macOS window tiling swallows
     ⌘⌥← / ⌘⌥→ before the app sees them, and browsers spend them on tab
     switching. */
  goBack: { key: "[", modifiers: ["command", "option"] },
  goForward: { key: "]", modifiers: ["command", "option"] },
  newChat: { key: "t", modifiers: ["command"] },
  newTask: { key: "n", modifiers: ["command"] },
  nextChat: { key: "]", modifiers: ["command"] },
  previousChat: { key: "[", modifiers: ["command"] },
  showKeyboardShortcuts: { key: "/", modifiers: ["command"] },
  toggleAssistant: { key: "j", modifiers: ["command"] },
  /* ⌘\ belonged to 1Password on the machines this ships to. */
  toggleSidebar: { key: "s", modifiers: ["command", "option"] },
  /* The right-hand pane's twin of ⌘⌥S. ⌘⌥I is the browser's developer tools,
     and ⌘⌥B is the chord editors already use for a secondary side bar. */
  toggleInspector: { key: "b", modifiers: ["command", "option"] },
  /* Settings and cancel-run share the Mac's chords. Browsers may claim ⌘, and
     ⌘. for themselves, which is exactly why these are rebindable. */
  openSettings: { key: ",", modifiers: ["command"] },
  cancelRun: { key: ".", modifiers: ["command"] },
  /* Desktop only: the browser has no editor to open. Listed so the account's
     bindings round-trip intact between the Mac and here. */
  openInEditor: { key: "o", modifiers: ["command", "shift"] },
  /* Desktop and iPad only: a browser opens a new window itself, and an org
     already opens in a new tab from the account menu. Listed for round-trip. */
  newWindow: { key: "n", modifiers: ["command", "shift"] },
  selectTask1: { key: "1", modifiers: ["command"] },
  selectTask2: { key: "2", modifiers: ["command"] },
  selectTask3: { key: "3", modifiers: ["command"] },
  selectTask4: { key: "4", modifiers: ["command"] },
  selectTask5: { key: "5", modifiers: ["command"] },
  selectTask6: { key: "6", modifiers: ["command"] },
  selectTask7: { key: "7", modifiers: ["command"] },
  selectTask8: { key: "8", modifiers: ["command"] },
  selectTask9: { key: "9", modifiers: ["command"] },
};

export type ShortcutSection = "General" | "Create" | "Chats" | "Tasks";
export type ShortcutCatalogItem = { command: ShortcutCommand; label: string; section: ShortcutSection };

export const SHORTCUT_CATALOG: ShortcutCatalogItem[] = [
  { command: "commandPalette", label: "Search and commands", section: "General" },
  { command: "showKeyboardShortcuts", label: "Keyboard shortcuts", section: "General" },
  { command: "focusPromptComposer", label: "Focus prompt composer", section: "General" },
  { command: "toggleAssistant", label: "Rivets assistant", section: "General" },
  { command: "toggleSidebar", label: "Toggle navigation", section: "General" },
  { command: "toggleInspector", label: "Toggle inspector", section: "General" },
  { command: "openSettings", label: "Settings", section: "General" },
  { command: "goBack", label: "Back", section: "General" },
  { command: "goForward", label: "Forward", section: "General" },
  { command: "newTask", label: "Create new task", section: "Create" },
  { command: "newChat", label: "New chat", section: "Create" },
  { command: "findInTranscript", label: "Find in transcript", section: "Chats" },
  { command: "copyConciseTranscript", label: "Copy concise transcript", section: "Chats" },
  { command: "cancelRun", label: "Cancel agent run", section: "Chats" },
  { command: "previousChat", label: "Previous chat tab", section: "Chats" },
  { command: "nextChat", label: "Next chat tab", section: "Chats" },
  { command: "closeChatTab", label: "Close chat tab", section: "Chats" },
  ...TASK_SHORTCUT_COMMANDS.map((command, index): ShortcutCatalogItem => ({
    command,
    label: `Switch to task ${index + 1}`,
    section: "Tasks",
  })),
];

export function shortcutKeyLabels(binding: ShortcutBinding): string[] {
  const modifiers = new Set(binding.modifiers);
  return [
    ...(modifiers.has("command") ? ["⌘"] : []),
    ...(modifiers.has("option") ? ["⌥"] : []),
    ...(modifiers.has("shift") ? ["⇧"] : []),
    ...(modifiers.has("control") ? ["⌃"] : []),
    binding.key.length === 1 ? binding.key.toUpperCase() : binding.key,
  ];
}

/** The chord as one printable string (⌘W) for menu rows and tooltips. */
export function shortcutChordLabel(binding: ShortcutBinding): string {
  return shortcutKeyLabels(binding).join("");
}

export function shortcutBindingsEqual(left: ShortcutBinding, right: ShortcutBinding): boolean {
  return left.key.toLowerCase() === right.key.toLowerCase()
    && MODIFIER_ORDER.every((modifier) => left.modifiers.includes(modifier) === right.modifiers.includes(modifier));
}

export function conflictingShortcutCommand(
  command: ShortcutCommand,
  binding: ShortcutBinding,
  bindings: ShortcutBindings,
): ShortcutCommand | undefined {
  return (Object.keys(bindings) as ShortcutCommand[])
    .find((candidate) => candidate !== command && shortcutBindingsEqual(bindings[candidate], binding));
}

function isShortcutBinding(value: unknown): value is ShortcutBinding {
  if (!value || typeof value !== "object") return false;
  const binding = value as Partial<ShortcutBinding> & { primary?: unknown; alt?: unknown; shift?: unknown };
  return typeof binding.key === "string" && binding.key.length === 1
    && Array.isArray(binding.modifiers)
    && binding.modifiers.length > 0
    && binding.modifiers.every((modifier) => MODIFIER_ORDER.includes(modifier))
    && new Set(binding.modifiers).size === binding.modifiers.length
    && binding.modifiers.some((modifier) => modifier !== "shift");
}

function mergeShortcutBindings(overrides: Partial<ShortcutBindings>): ShortcutBindings {
  return Object.fromEntries((Object.keys(DEFAULT_SHORTCUT_BINDINGS) as ShortcutCommand[])
    .map((command) => [command, isShortcutBinding(overrides[command]) ? overrides[command] : DEFAULT_SHORTCUT_BINDINGS[command]])) as ShortcutBindings;
}

export function shortcutBindingOverrides(bindings: ShortcutBindings): Partial<ShortcutBindings> {
  return Object.fromEntries((Object.keys(bindings) as ShortcutCommand[])
    .filter((command) => !shortcutBindingsEqual(bindings[command], DEFAULT_SHORTCUT_BINDINGS[command]))
    .map((command) => [command, bindings[command]]));
}

type CachedShortcutSettings = { bindings: Partial<ShortcutBindings>; updatedAt: number; dirty: boolean };

function loadAccountCache(userId: string): CachedShortcutSettings | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    const parsed = JSON.parse(window.localStorage.getItem(`${SHORTCUT_STORAGE_PREFIX}:${userId}`) ?? "null") as CachedShortcutSettings | null;
    if (!parsed || typeof parsed.updatedAt !== "number" || typeof parsed.dirty !== "boolean") return undefined;
    return { bindings: shortcutBindingOverrides(mergeShortcutBindings(parsed.bindings)), updatedAt: parsed.updatedAt, dirty: parsed.dirty };
  } catch {
    return undefined;
  }
}

function saveAccountCache(userId: string | undefined, bindings: ShortcutBindings, updatedAt: number, dirty: boolean) {
  if (typeof window === "undefined" || !userId) return;
  try {
    window.localStorage.setItem(`${SHORTCUT_STORAGE_PREFIX}:${userId}`, JSON.stringify({
      bindings: shortcutBindingOverrides(bindings), updatedAt, dirty,
    } satisfies CachedShortcutSettings));
  } catch { /* Keep the in-memory setting when storage is unavailable. */ }
}

type KeyboardShortcutSettings = {
  bindings: ShortcutBindings;
  userId?: string;
  updatedAt: number;
  dirty: boolean;
  revision: number;
  capturing: boolean;
  setBinding: (command: ShortcutCommand, binding: ShortcutBinding) => void;
  resetBinding: (command: ShortcutCommand) => void;
  configureAccount: (userId: string, remote: KeyboardShortcutPreferences) => void;
  applyRemote: (remote: KeyboardShortcutPreferences) => void;
  setCapturing: (capturing: boolean) => void;
};

/** Account-synced settings with an account-scoped offline cache. */
export const useKeyboardShortcutSettings = create<KeyboardShortcutSettings>((set) => ({
  bindings: DEFAULT_SHORTCUT_BINDINGS,
  updatedAt: 0,
  dirty: false,
  revision: 0,
  capturing: false,
  setBinding: (command, binding) => set((state) => {
    const bindings = { ...state.bindings, [command]: binding };
    saveAccountCache(state.userId, bindings, state.updatedAt, true);
    return { bindings, dirty: true, revision: state.revision + 1 };
  }),
  resetBinding: (command) => set((state) => {
    const bindings = { ...state.bindings, [command]: DEFAULT_SHORTCUT_BINDINGS[command] };
    saveAccountCache(state.userId, bindings, state.updatedAt, true);
    return { bindings, dirty: true, revision: state.revision + 1 };
  }),
  configureAccount: (userId, remote) => set((state) => {
    if (state.userId === userId) {
      if (state.dirty || remote.updatedAt < state.updatedAt) return state;
      const bindings = mergeShortcutBindings(remote.bindings);
      saveAccountCache(userId, bindings, remote.updatedAt, false);
      return { bindings, updatedAt: remote.updatedAt, dirty: false };
    }
    const cache = loadAccountCache(userId);
    if (cache?.dirty || (!cache && state.dirty)) {
      const bindings = cache ? mergeShortcutBindings(cache.bindings) : state.bindings;
      saveAccountCache(userId, bindings, cache?.updatedAt ?? 0, true);
      return { userId, bindings, updatedAt: cache?.updatedAt ?? 0, dirty: true, revision: state.revision + 1 };
    }
    const bindings = mergeShortcutBindings(remote.bindings);
    saveAccountCache(userId, bindings, remote.updatedAt, false);
    return { userId, bindings, updatedAt: remote.updatedAt, dirty: false, revision: state.revision + 1 };
  }),
  applyRemote: (remote) => set((state) => {
    const bindings = mergeShortcutBindings(remote.bindings);
    saveAccountCache(state.userId, bindings, remote.updatedAt, false);
    return { bindings, updatedAt: remote.updatedAt, dirty: false };
  }),
  setCapturing: (capturing) => set({ capturing }),
}));

export async function syncKeyboardShortcutSettings(userId: string, remote: KeyboardShortcutPreferences) {
  const store = useKeyboardShortcutSettings.getState();
  store.configureAccount(userId, remote);
  const configured = useKeyboardShortcutSettings.getState();
  if (!configured.dirty) return;
  const revision = configured.revision;
  const response = await client.updateKeyboardShortcutPreferences({ bindings: shortcutBindingOverrides(configured.bindings) });
  const current = useKeyboardShortcutSettings.getState();
  if (current.userId === userId && current.revision === revision) current.applyRemote(response.keyboardShortcuts);
  else if (current.userId === userId && current.dirty) await persistKeyboardShortcutSettings();
}

export async function persistKeyboardShortcutSettings() {
  const store = useKeyboardShortcutSettings.getState();
  if (!store.userId || !store.dirty) return;
  const userId = store.userId;
  const revision = store.revision;
  const response = await client.updateKeyboardShortcutPreferences({ bindings: shortcutBindingOverrides(store.bindings) });
  const current = useKeyboardShortcutSettings.getState();
  if (current.userId === userId && current.revision === revision) current.applyRemote(response.keyboardShortcuts);
  else if (current.userId === userId && current.dirty) await persistKeyboardShortcutSettings();
}

type ShortcutEvent = Pick<KeyboardEvent, "key" | "metaKey" | "ctrlKey" | "shiftKey" | "altKey">;
type ShortcutHandler = () => boolean | void;
type RegisteredHandler = { handler: ShortcutHandler; priority: number; order: number };

const handlers = new Map<ShortcutCommand, Set<RegisteredHandler>>();
let registrationOrder = 0;

export function commandForKeyboardEvent(
  event: ShortcutEvent,
  bindings: ShortcutBindings = DEFAULT_SHORTCUT_BINDINGS,
): ShortcutCommand | undefined {
  return (Object.keys(bindings) as ShortcutCommand[]).find((command) => {
    const binding = bindings[command];
    const modifiers = new Set(binding.modifiers);
    return event.key.toLowerCase() === binding.key.toLowerCase()
      && event.metaKey === modifiers.has("command")
      && event.ctrlKey === modifiers.has("control")
      && event.shiftKey === modifiers.has("shift")
      && event.altKey === modifiers.has("option");
  });
}

/** Runs the highest-priority mounted handler that accepts the command. */
export function runShortcutCommand(command: ShortcutCommand, minimumPriority = -Infinity): boolean {
  const registered = [...(handlers.get(command) ?? [])]
    .filter((entry) => entry.priority >= minimumPriority)
    .sort((a, b) => b.priority - a.priority || b.order - a.order);
  for (const entry of registered) {
    if (entry.handler() !== false) return true;
  }
  return false;
}

export function registerShortcutCommand(
  command: ShortcutCommand,
  handler: ShortcutHandler,
  priority = 0,
): () => void {
  const entry = { handler, priority, order: registrationOrder++ };
  const commandHandlers = handlers.get(command) ?? new Set<RegisteredHandler>();
  commandHandlers.add(entry);
  handlers.set(command, commandHandlers);
  return () => {
    commandHandlers.delete(entry);
    if (commandHandlers.size === 0) handlers.delete(command);
  };
}

/** Installs the single global listener using the current account's cached bindings. */
export function useAppKeyboardShortcuts() {
  const bindings = useKeyboardShortcutSettings((state) => state.bindings);
  const capturing = useKeyboardShortcutSettings((state) => state.capturing);
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || capturing) return;
      const command = commandForKeyboardEvent(event, bindings);
      if (!command || !runShortcutCommand(command)) return;
      // Browsers reserve a few of these chords (⌘W above all) and never deliver
      // the event at all, so this cannot always win. Where the page does see the
      // key — installed PWAs, an Electron shell — preventing the default is what
      // keeps the browser from acting on a chord the app just handled.
      event.preventDefault();
    };
    window.addEventListener("keydown", onKeyDown, { capture: true });
    return () => window.removeEventListener("keydown", onKeyDown, { capture: true });
  }, [bindings, capturing]);
}

/** Registers a contextual command without making shortcut bindings a component concern. */
export function useShortcutCommand(
  command: ShortcutCommand,
  handler: ShortcutHandler,
  { enabled = true, priority = 0 }: { enabled?: boolean; priority?: number } = {},
) {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    if (!enabled) return;
    return registerShortcutCommand(command, () => handlerRef.current(), priority);
  }, [command, enabled, priority]);
}

/** Registers the fixed number row without making callers repeat nine hooks. */
export function useTaskShortcutCommands(handler: (zeroBasedIndex: number) => boolean | void) {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    const unregister = TASK_SHORTCUT_COMMANDS.map((command, index) =>
      registerShortcutCommand(command, () => handlerRef.current(index)),
    );
    return () => unregister.forEach((remove) => remove());
  }, []);
}
